import { useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import { useDispatch } from "react-redux";
import { Button, Dialog } from "@mui/material";
import { Watch } from "@mui/icons-material";
import { useNFC } from "../../../../../../nfc/useNFC";
import { updateEntry } from "../../../../../../../actions/entry";
import { useNFCEvents } from "../../../../../../nfc/useNFCEvents";

import { GET_NFC_TAG } from "../../../../../../../graphql/nfcTag";
import { NFCNotSupported } from "../../../../../../layout/ErrorPages";
import { ScanEntriesDialog } from "./ScanEntriesDialog";
import { T } from "../../../../../../util/t";
import { useNFCDialogState } from "../../../../../../nfc/useNFCDialogState";

const findEntryForIndividual = ({ competitors, tagId, athleteId }) => {
    const entry = athleteId
        ? competitors.find(
              (competitor) => "" + competitor.athlete.id === athleteId,
          )
        : competitors.find((competitor) =>
              competitor.athlete.nfcTags?.some((tag) => tag.id === tagId),
          );
    return [entry, { status: "confirmed", nfc_tag_id: tagId }];
};

const findEntryForTeam = ({ competitors, tagId, athleteId }) => {
    const entry = athleteId
        ? competitors.find((competitor) =>
              competitor.teamMembers?.some(
                  (teamMember) => "" + teamMember.athlete.id === athleteId,
              ),
          )
        : competitors.find((competitor) =>
              competitor.teamMembers?.some((teamMember) =>
                  teamMember.athlete.nfcTags?.some((tag) => tag.id === tagId),
              ),
          );

    return [
        entry,
        {
            status: "confirmed",
            nfc_tag_id: tagId,
            team_members_attributes: entry?.teamMembers?.map((teamMember) => ({
                id: teamMember.id,
                nfc_tag_id: athleteId
                    ? "" + teamMember.athlete.id === athleteId
                        ? tagId
                        : teamMember.athlete.nfcTags?.[0]?.id
                    : teamMember.athlete.nfcTags?.some(
                            (tag) => tag.id === tagId,
                        )
                      ? tagId
                      : teamMember.athlete.nfcTags?.[0]?.id,
            })),
        },
    ];
};

export const ScanEntriesWidget = ({ isTeams, registered, alternates }) => {
    const dispatch = useDispatch();
    const competitors = registered.concat(alternates);
    const [event, setEvent, setDisappearingEvent, clearEvent] = useNFCEvents();

    const client = useApolloClient();

    const confirmEntry = useCallback(
        (entry, updateValues) =>
            dispatch(updateEntry(entry.eventDivisionId, entry.id, updateValues))
                .then(() => setDisappearingEvent({ type: "success" }))
                .catch((e) => {
                    setDisappearingEvent({ type: "warning" });
                    throw e;
                }),
        [],
    );

    const onReading = useCallback(
        ({ serialNumber, humanReadableId }) => {
            const [entry, updateValues] = (
                isTeams ? findEntryForTeam : findEntryForIndividual
            )({ competitors, tagId: serialNumber });
            return entry
                ? confirmEntry(entry, updateValues)
                : client
                      .query({
                          query: GET_NFC_TAG,
                          variables: { id: serialNumber, humanReadableId },
                          fetchPolicy: "network-only",
                      })
                      .then(({ data }) => {
                          const athlete = data?.nfcTag?.athlete;
                          const [entry, updateValues] = (
                              isTeams
                                  ? findEntryForTeam
                                  : findEntryForIndividual
                          )({
                              competitors,
                              tagId: serialNumber,
                              athleteId: athlete?.id,
                          });
                          return entry
                              ? confirmEntry(entry, updateValues)
                              : Promise.reject(
                                    setEvent({
                                        type: "error",
                                        athlete,
                                        tagId: serialNumber,
                                        humanReadableId,
                                    }),
                                );
                      });
        },
        [isTeams, competitors],
    );

    const onReadingError = useCallback(
        () => setDisappearingEvent({ type: "warning" }),
        [],
    );
    const onBadTag = useCallback(() => setEvent({ type: "error" }), []);
    const onLoading = useCallback(() => setEvent({ type: "loading" }), []);

    const { nfcEnabled, startScanning, stopScanning } = useNFC({
        onReading,
        onReadingError,
        onBadTag,
        onLoading,
    });
    const [open, openDialog, closeDialog] = useNFCDialogState({
        setEvent,
        startScanning,
        stopScanning,
    });

    return (
        <>
            <Button
                variant="outlined"
                size="small"
                onClick={openDialog}
                startIcon={<Watch />}
            >
                <T>Scan to confirm</T>
            </Button>
            <Dialog fullScreen open={open} onClose={closeDialog}>
                {nfcEnabled ? (
                    <ScanEntriesDialog
                        event={event}
                        clearEvent={clearEvent}
                        setDisappearingEvent={setDisappearingEvent}
                        registered={registered}
                        closeDialog={closeDialog}
                        isTeams={isTeams}
                    />
                ) : (
                    <NFCNotSupported closeDialog={closeDialog} />
                )}
            </Dialog>
        </>
    );
};
